import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = () => ({
  "Content-Type": "application/json",
  // Tell Laravel this is an XHR/JSON request — without this header the
  // framework's exception renderer can decide to serve an HTML error
  // page for "browser" clients and the response.json() call below
  // crashes with "unexpected character at line 1 column 1".
  Accept: "application/json",
  "X-Requested-With": "XMLHttpRequest",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/**
 * Replacement for `await res.json()` that never throws a cryptic
 * SyntaxError when the server returns HTML (Apache 403, login redirect,
 * Whoops debug page, gateway timeout, empty body, etc.). Always returns
 * a usable error shape with a human-readable `message`.
 */
async function safeJson(res: Response): Promise<any> {
  const contentType = res.headers.get("content-type") ?? "";
  const text = await res.text();

  if (contentType.includes("application/json") || /^[\s﻿]*[\[{]/.test(text)) {
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`Server returned malformed JSON (HTTP ${res.status}).`);
    }
  }

  // Non-JSON body — convert to a friendly diagnostic. Status-specific
  // messages first so the operator gets a clue without opening DevTools.
  let message: string;
  if (res.status === 401)      message = "Your session has expired. Please log in again.";
  else if (res.status === 403) message = "Forbidden. Your account does not have access (or a server filter rejected the request).";
  else if (res.status === 404) message = "Endpoint not found.";
  else if (res.status === 419) message = "Session token mismatch. Refresh and try again.";
  else if (res.status >= 500)  message = `Server error (HTTP ${res.status}). Check Laravel logs.`;
  else if (/^\s*<!doctype html|^\s*<html/i.test(text)) message = `Server returned an HTML page instead of JSON (HTTP ${res.status}).`;
  else if (text.trim() === "") message = `Empty response from server (HTTP ${res.status}).`;
  else                         message = `Unexpected response from server (HTTP ${res.status}).`;
  throw new Error(message);
}

// ── Privileges ────────────────────────────────────────────────────────
export interface IPrivilege {
  id: number;
  module: string;
  display_name: string;
}

export const fetchPrivileges = async (): Promise<IPrivilege[]> => {
  const res = await trackedFetch(`${API_URL}/privileges`, { headers: headers() });
  const json = await safeJson(res);
  return json.data ?? [];
};

export const fetchModules = async (): Promise<Record<string, string>> => {
  const res = await trackedFetch(`${API_URL}/privileges/modules`, { headers: headers() });
  const json = await safeJson(res);
  return json.data ?? {};
};

// ── Roles ─────────────────────────────────────────────────────────────
export interface IRolePrivilege {
  id?: number;
  role_id?: number;
  module: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface IRole {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  users_count?: number;
  privileges: IRolePrivilege[];
}

export const fetchRoles = async (): Promise<IRole[]> => {
  const res = await trackedFetch(`${API_URL}/roles`, { headers: headers() });
  const json = await safeJson(res);
  return json.data ?? [];
};

export const fetchRole = async (id: number): Promise<IRole> => {
  const res = await trackedFetch(`${API_URL}/roles/${id}`, { headers: headers() });
  const json = await safeJson(res);
  return json.data;
};

export const createRole = async (data: { name: string; description: string; privileges: IRolePrivilege[] }): Promise<IRole> => {
  const res = await trackedFetch(`${API_URL}/roles`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(data),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const updateRole = async (id: number, data: { name: string; description: string; privileges: IRolePrivilege[] }): Promise<IRole> => {
  // POST + `_method=PUT` spoof — same Apache/mod_security workaround as
  // updateUser; Laravel routes it back to the PUT controller transparently.
  const res = await trackedFetch(`${API_URL}/roles/${id}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ ...data, _method: "PUT" }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const deleteRole = async (id: number): Promise<void> => {
  // POST + `_method=DELETE` spoof — see deleteUser for context.
  const res = await trackedFetch(`${API_URL}/roles/${id}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ _method: "DELETE" }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
};

// ── Users ─────────────────────────────────────────────────────────────
export interface IUserManaged {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  profile_image: string | null;
  role_id: number | null;
  role_name: string;
  created_at: string;
}

export const fetchUsers = async (): Promise<IUserManaged[]> => {
  const res = await trackedFetch(`${API_URL}/users`, { headers: headers() });
  const json = await safeJson(res);
  return json.data ?? [];
};

/**
 * Live duplicate-phone check for the Add/Edit User form.
 * Scoped strictly to the USERS table — customers / leads sharing the
 * same phone are NOT considered duplicates by this endpoint.
 */
export interface IUserDuplicateCheck {
  duplicate: boolean;
  user: { id: number; name: string; email: string | null } | null;
}
export const checkUserDuplicatePhone = async (
  phone: string,
  excludeId?: number,
): Promise<IUserDuplicateCheck> => {
  const params = new URLSearchParams({ phone });
  if (excludeId) params.set("exclude_id", String(excludeId));
  const res = await trackedFetch(`${API_URL}/users/check-duplicate-phone?${params.toString()}`, {
    headers: headers(),
  });
  const json = await safeJson(res);
  return (json.data as IUserDuplicateCheck) ?? { duplicate: false, user: null };
};

export const createUser = async (data: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  profile_image?: string | null;
  password?: string;
  role_id: number;
}): Promise<{ data: IUserManaged; plain_password: string }> => {
  const res = await trackedFetch(`${API_URL}/users`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(data),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return { data: json.data, plain_password: json.plain_password };
};

export const sendUserCredentials = async (id: number, password: string): Promise<{ message: string }> => {
  const res = await trackedFetch(`${API_URL}/users/${id}/send-credentials`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ password }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return { message: json.message };
};

export const sendUserCredentialsSms = async (
  id: number,
  password: string,
): Promise<{ message: string; sms_sent: boolean }> => {
  const res = await trackedFetch(`${API_URL}/users/${id}/send-credentials-sms`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ password }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return { message: json.message, sms_sent: json.sms_sent ?? true };
};

export const updateUser = async (id: number, data: {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  profile_image?: string | null;
  password?: string;
  role_id?: number;
}): Promise<any> => {
  // Use POST + Laravel method spoofing (`_method=PUT`) instead of a raw
  // PUT. The Apache/mod_security config on some host setups silently
  // rejects PUT requests whose body contains certain field combinations
  // (e.g. password + email together) and returns the default HTML 403
  // page — which then crashes JSON.parse on the client with
  // "unexpected character at line 1 column 1". POST sails through the
  // same filter and Laravel's HandleMethods middleware routes it to the
  // PUT controller transparently. Backend signature is unchanged.
  const res = await trackedFetch(`${API_URL}/users/${id}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ ...data, _method: "PUT" }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return json.data;
};

export const deleteUser = async (id: number): Promise<void> => {
  // Same POST + `_method=DELETE` spoof as updateUser — Apache/mod_security
  // on live rejects the raw DELETE verb with a generic HTML 403 page that
  // surfaces to the operator as "Forbidden. Your account does not have
  // access (or a server filter rejected the request)." Laravel's
  // HandleMethods middleware translates the spoof back to DELETE before
  // routing, so the backend controller is hit identically.
  const res = await trackedFetch(`${API_URL}/users/${id}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ _method: "DELETE" }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
};

export const sendUserResetLink = async (
  id: number
): Promise<{ message: string; email_sent: boolean; reset_url: string | null }> => {
  const res = await trackedFetch(`${API_URL}/users/${id}/send-reset-link`, {
    method: "POST",
    headers: headers(),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return { message: json.message, email_sent: json.email_sent ?? true, reset_url: json.reset_url ?? null };
};

export const sendUserResetSms = async (
  id: number
): Promise<{ message: string; sms_sent: boolean; reset_url: string | null }> => {
  const res = await trackedFetch(`${API_URL}/users/${id}/send-reset-sms`, {
    method: "POST",
    headers: headers(),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return { message: json.message, sms_sent: json.sms_sent ?? true, reset_url: json.reset_url ?? null };
};

export const setUserPassword = async (id: number, password: string): Promise<string> => {
  const res = await trackedFetch(`${API_URL}/users/${id}/set-password`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ password }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message);
  return json.message;
};

/* ── Public self-serve password reset ──────────────────────────────────
 * These three endpoints are UNAUTHENTICATED — no Authorization header
 * is sent so an expired-token user landing on /reset-password can still
 * complete the flow. The reset page is routed OUTSIDE the LoginProvider
 * gate in App.tsx, so the user reaches it before any auth check.
 *
 * The forgot endpoint never reveals whether the identifier matched —
 * it always returns a generic "if an account exists, a link was sent"
 * message. Don't render branch-specific UI based on the response.
 * ──────────────────────────────────────────────────────────────────── */

const publicHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-Requested-With": "XMLHttpRequest",
});

export const requestPasswordReset = async (
  identifier: string,
  method: "email" | "sms",
): Promise<{ message: string }> => {
  const res = await trackedFetch(`${API_URL}/auth/forgot-password`, {
    method: "POST",
    headers: publicHeaders(),
    body: JSON.stringify({ identifier, method }),
  });
  const json = await safeJson(res);
  // Rate limit / validation errors still bubble through `safeJson` with
  // a status flag the UI can show. Anything else is a generic success.
  if (json.status === false && json.code !== 200) {
    throw new Error(json.message || "Reset request failed.");
  }
  return { message: json.message };
};

export interface ResetTokenValidation {
  valid: boolean;
  message?: string;
  user?: { first_name: string | null; display_name: string | null } | null;
}

export const validateResetToken = async (token: string): Promise<ResetTokenValidation> => {
  const params = new URLSearchParams({ token });
  const res = await trackedFetch(
    `${API_URL}/auth/reset-password/validate?${params.toString()}`,
    { headers: publicHeaders() },
  );
  const json = await safeJson(res);
  return {
    valid: !!json.valid,
    message: json.message,
    user: json.user ?? null,
  };
};

export const submitPasswordReset = async (
  token: string,
  password: string,
  passwordConfirmation: string,
): Promise<{ message: string }> => {
  const res = await trackedFetch(`${API_URL}/auth/reset-password`, {
    method: "POST",
    headers: publicHeaders(),
    body: JSON.stringify({
      token,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });
  const json = await safeJson(res);
  if (!json.status) throw new Error(json.message || "Password reset failed.");
  return { message: json.message };
};
